import { useTranslation } from "react-i18next";
import type { AnalysisEnvelope, KeyDriver } from "../api/types";
import { BiasBadge, ConvictionBar, directionTone, toneClass } from "../components/BiasBadge";
import { countdown, percent } from "../components/format";
import { AnalysisExpansion } from "./AnalysisExpansion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { NotesBadge } from "@/notes/NotesBadge";
import { AddNoteButton } from "@/notes/AddNoteButton";
import { AskAboutButton } from "@/copilot/AskAboutButton";
import { LanguageBadge } from "@/i18n/LanguageBadge";
import { cn } from "@/lib/utils";

interface StickySignalCardProps {
  analysis: AnalysisEnvelope | null;
  materialId: string;
  nextRunAt: string | null;
  now: number;
  expanded: boolean;
  notesCount?: number;
  /**
   * i18n Slice 2 — the user's preferred language (from useLanguage()).
   * Optional so existing test renderers without a LanguageProvider keep
   * working; null when unknown.
   */
  requestedLanguage?: string;
  /**
   * The language the API actually returned in the analysis envelope
   * (post-English-fallback). Null when the analysis envelope hasn't
   * resolved yet — badge stays hidden until both sides are known.
   */
  servedLanguage?: string | null;
  onToggleExpanded: () => void;
  onClusterJump: (clusterId: string) => void;
  onOpenNotes?: () => void;
}

/**
 * Map a driver's direction to a directional arrow. Exported so the unit
 * tests can exercise the enum branches directly.
 *
 * IMPORTANT (Slice 1 / F7):
 * The plan target was `direction: "up" | "down" | "sideways"` — a strict
 * enum that doesn't depend on English text. As of 2026-05-25 the
 * collector still emits `direction: string` (free-form labels such as
 * "Higher demand" or "yields lower"); the enum migration is pending on
 * the API side. We accept either shape:
 *
 *   1. If the value matches a known enum literal, route directly.
 *   2. Otherwise fall back to the legacy substring sniff so existing
 *      payloads keep producing the right glyph.
 *
 * Once the backend emits the enum natively, the fallback branch can be
 * deleted along with this comment.
 */
export type DriverDirection = "up" | "down" | "sideways" | string;

export function driverArrowFromDirection(direction: DriverDirection): string {
  switch (direction) {
    case "up":
      return "↑";
    case "down":
      return "↓";
    case "sideways":
      return "→";
  }
  // Legacy free-form string path — sniff English keywords.
  const lower = direction.toLowerCase();
  if (lower.includes("bear") || lower.includes("down") || lower.includes("lower")) {
    return "↓";
  }
  if (lower.includes("bull") || lower.includes("up") || lower.includes("higher")) {
    return "↑";
  }
  return "→";
}

function driverArrow(driver: KeyDriver): string {
  return driverArrowFromDirection(driver.direction);
}

export function StickySignalCard({
  analysis,
  materialId,
  nextRunAt,
  now,
  expanded,
  notesCount = 0,
  requestedLanguage,
  servedLanguage,
  onToggleExpanded,
  onClusterJump,
  onOpenNotes,
}: StickySignalCardProps) {
  const { t } = useTranslation("dashboard");
  const body = analysis?.body ?? null;
  const bias = body?.directional_bias;
  const topDrivers = body?.key_drivers.slice(0, 3) ?? [];

  return (
    <Card className="sticky top-0 z-10 m-3 mb-0 py-0 gap-0 border-l-4 border-l-primary bg-card shadow-md" aria-label={t("sticky.ariaLabel")}>
      <CardContent className="p-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide font-semibold text-primary">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary animate-pulse" aria-hidden="true" />
              {t("sticky.pinned")}
            </span>
            {/* i18n Slice 2 — shows "Available in English" (or similar)
                when the analysis fell back from the user's requested
                language. Renders nothing when requested === served. */}
            {requestedLanguage && servedLanguage ? (
              <LanguageBadge
                requestedLanguage={requestedLanguage}
                servedLanguage={servedLanguage}
              />
            ) : null}
          </div>
          {onOpenNotes != null ? (
            <div className="flex items-center gap-1">
              <NotesBadge count={notesCount} onClick={onOpenNotes} />
              <AddNoteButton onClick={onOpenNotes} />
              {analysis ? (
                // The agent receives {kind: "analysis", id: analysis.id} from
                // the asked_about readable; the pane's materialId makes a
                // human-readable label in the confirm-card target description.
                <AskAboutButton
                  target={{
                    kind: "analysis",
                    id: analysis.id,
                    title: materialId,
                  }}
                />
              ) : null}
            </div>
          ) : null}
        </div>
        {body && bias ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <BiasBadge bias={bias} />
                <span className="text-xs text-muted-foreground">{bias.horizon}</span>
              </div>
              <span className="text-xs text-muted-foreground">
                {t("sticky.nextRun", { value: countdown(nextRunAt, now) })}
              </span>
            </div>
            <ConvictionBar value={bias.conviction} showLabel={false} />
            <p className="text-sm">{body.fresh_news_summary}</p>
            <div className="flex flex-wrap gap-1.5" aria-label={t("sticky.driversAria")}>
              {topDrivers.map((driver, index) => {
                const tone = directionTone(`${driver.direction} ${driver.driver}`);
                return (
                  <Button
                    key={`${driver.supporting_cluster_id}-${index}`}
                    variant="secondary"
                    size="sm"
                    className={cn(
                      "h-auto px-2 py-1 text-xs font-mono border hover:opacity-90",
                      toneClass(tone),
                    )}
                    onClick={() => onClusterJump(driver.supporting_cluster_id)}
                    title={`${driver.direction}: ${driver.driver}`}
                  >
                    {driverArrow(driver)} {driver.driver} {percent(driver.weight)}
                  </Button>
                );
              })}
              {topDrivers.length === 0 ? (
                <span className="text-xs text-muted-foreground">{t("sticky.noDrivers")}</span>
              ) : null}
            </div>
            <Button variant="link" size="sm" onClick={onToggleExpanded} className="h-auto p-0">
              {expanded ? t("sticky.hideDetails") : t("sticky.showDetails")}
            </Button>
            {expanded && analysis ? (
              <AnalysisExpansion analysis={analysis} onClusterJump={onClusterJump} />
            ) : null}
          </>
        ) : (
          <div className="text-sm text-muted-foreground">{t("sticky.noAnalysis")}</div>
        )}
      </CardContent>
    </Card>
  );
}
